import { FormattedMessage, defineMessages } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, BrandXL } from '@sorare/core/src/atoms/typography';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2026/NewSeasonLogo';
import { Season26TutorialDialog } from '@sorare/core/src/components/dialog/Season26TutorialDialog';
import { Season26TutorialStep } from '@sorare/core/src/components/dialog/Season26TutorialStep';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { ANY_SPORT_ESSENCE_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DrawCards } from './DrawCards';
import { PickACard } from './PickACard';
import { CompleteLimitedLineup } from '../IntroducingRoadToPro/CompleteLimitedLineup';

const messages = defineMessages({
  title: {
    id: 'CraftUnlockedDialog.title',
    defaultMessage: 'Crafting Cards',
  },
});
type Props = {
  open: boolean;
  onClose: () => void;
};

export const CraftUnlockedDialog = ({ open, onClose }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { generateSportContextPath } = useSportContext();
  return (
    <Season26TutorialDialog
      open={open}
      color="var(--c-yellow-600)"
      textColor="var(--c-black)"
      titleGroup={
        isTabletAndAbove ? (
          <Vertical gap={4} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandM color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandM>
              <BrandXL>
                <FormattedMessage {...messages.title} />
              </BrandXL>
            </Vertical>
          </Vertical>
        ) : (
          <Vertical gap={2} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandS>
              <BrandM>
                <FormattedMessage {...messages.title} />
              </BrandM>
            </Vertical>
          </Vertical>
        )
      }
      items={[
        <Season26TutorialStep stepNb={1} key="1">
          <DrawCards />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={2} key="2">
          <PickACard />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={3} key="3">
          <CompleteLimitedLineup />
        </Season26TutorialStep>,
      ]}
      footer={
        <Vertical center>
          <OnboardingButton
            size="large"
            color="primary"
            to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: Rarity.limited },
            })}
            onClick={() => {
              onClose();
            }}
          >
            <FormattedMessage
              id="CraftUnlockedDialog.cta"
              defaultMessage="Craft my card"
            />
          </OnboardingButton>
        </Vertical>
      }
    />
  );
};
